import "./style.css";
import { Skeleton } from "@/components/other";
import { Typography } from "@/components/text";
import { cn } from "@/libraries/cn";
import { getStatusColor } from "@/libraries/colors";
import {
	getMessageTagLabel,
	localizeMessageTag,
	pluralize,
} from "@/libraries/helper";
import { getMessageQueryOptions } from "@/queries";
import { MESSAGE_TAG } from "@/schemas";
import type { MessageDiscussion } from "@/types";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Chip from "@mui/material/Chip";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import dayjs from "dayjs";
import isEqual from "fast-deep-equal";
import { orderBy } from "lodash";
import isString from "lodash/isString";
import { Suspense, lazy, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import MessageAction from "./Action";
import { FlagIcons } from "./FlagIcons";

const MarkdownMessage = lazy(() => import("./MarkdownMessage"));
const PublishBox = lazy(() => import("./Publish"));
const MessageSystem = lazy(() => import("./System"));
const MediaBox = lazy(() => import("./Media"));
const AskHemiShareCard = lazy(() => import("./AskHemiShareCard"));
const ReplyBox = lazy(() => import("./Replys"));

interface MessageItemProps extends React.ComponentProps<"div"> {
	chatModule: "chats" | "cases";
	message: MessageDiscussion;
	members?: { id: number; name: string }[];
	profileId: number;
	senderName?: string;
	enableFetchMessage?: boolean;
	onClickReply?: (e: React.MouseEvent<HTMLDivElement>) => void;
	onClickContextMessage?: (
		e: React.MouseEvent<HTMLDivElement>,
		message: string,
	) => void;
}

const MessageItem = (props: MessageItemProps) => {
	const {
		chatModule = "chats",
		message,
		members = [],
		profileId,
		onClickReply,
		className,
		senderName,
		enableFetchMessage = false,
		onClickContextMessage,
		...divProps
	} = props;
	const { t } = useTranslation();

	const searchQ = useSearch({
		strict: false,
		select: (s) => s?.q,
	});

	const messageQuery = useQuery(
		getMessageQueryOptions(message.id_db, {
			enabled: enableFetchMessage && +message.id_db > 0,
		}),
	);

	const isMe = message?.user?.id === profileId;

	const sender = useMemo(() => {
		let sender = senderName || `${message?.user?.fullname}`;
		if (isMe) sender += ` (${t("me")})`;
		return sender;
	}, [isMe, senderName, message?.user?.fullname, t]);

	const text = useMemo(() => {
		if (message?.is_deleted) {
			return "This message was deleted";
		}
		if (message?.text) {
			return message.text;
		}
		return null;
	}, [message]);

	const tagLabel = useMemo(
		() => (message ? getMessageTagLabel(message, chatModule) : ""),
		[chatModule, message],
	);

	const isExternal =
		chatModule === "cases" &&
		!!tagLabel &&
		isString(tagLabel) &&
		["referral", "advice", "handover"].includes(tagLabel);

	const messages = useMemo(() => {
		if (!messageQuery.data) return [];
		const items = messageQuery.data.message.messages.filter(
			(m) => m.id !== message.id,
		);
		return orderBy(items, "updated_at", "asc");
	}, [messageQuery.data, message]);

	const isMyAiQuestion = useMemo(
		() =>
			message.tags === MESSAGE_TAG.AI &&
			!message.hemi_responses?.length &&
			message._isMe,
		[message],
	);

	const memberMentions = useMemo(
		() =>
			message?.mentions?.length
				? message.mentions.map((m) => ({ id: m.id, name: m.fullname }))
				: members,
		[members, message?.mentions],
	);

	if (
		message?.system_message &&
		message.text &&
		!message.hemi_responses?.length
	) {
		return (
			<Suspense>
				<MessageSystem message={message} />
			</Suspense>
		);
	}

	if (message?.feature === "askhemi_share" && !message.is_deleted) {
		return (
			<div className={cn("flex flex-col", className)} {...divProps}>
				<Suspense>
					<AskHemiShareCard message={message} />
				</Suspense>
			</div>
		);
	}

	return (
		<div
			id={`msg-${message.id}`}
			data-message-id={message.id}
			data-module={chatModule}
			data-tag={!isMyAiQuestion ? message.tags : undefined}
			data-me={isMe}
			className={cn("message-box", className)}
			{...divProps}
		>
			{!isMyAiQuestion ? (
				<div
					className={cn("flex", {
						"flex-row": !isMe,
						"flex-row-reverse": isMe,
					})}
				>
					{isString(tagLabel) || (Array.isArray(tagLabel) && !!tagLabel[1]) ? (
						<Typography
							fontWeight={500}
							fontSize={12}
							className={cn("badge", {
								"bg-blue-700 text-white": chatModule === "chats",
								"bg-amber-400": chatModule === "cases",
							})}
						>
							{localizeMessageTag(
								isString(tagLabel) ? tagLabel : tagLabel[1],
								t,
							)}
						</Typography>
					) : null}
					{isExternal || (Array.isArray(tagLabel) && !!tagLabel[0]) ? (
						<Typography
							fontWeight={500}
							fontSize={12}
							className="badge bg-amber-100 text-amber-500"
						>
							{localizeMessageTag(isExternal ? "external" : tagLabel[0], t)}
						</Typography>
					) : null}
				</div>
			) : null}
			<Card
				variant="outlined"
				className={cn("!rounded-xl", {
					...(!isMyAiQuestion
						? {
								"!border-[3px]": !!tagLabel,
								"!rounded-tr-none": !!tagLabel && isMe,
								"!rounded-tl-none": !!tagLabel && !isMe,
								"!border-blue-700": !!tagLabel && chatModule === "chats",
								"!border-amber-400": !!tagLabel && chatModule === "cases",
							}
						: {}),
					"!bg-brand": isMyAiQuestion || (isMe && !tagLabel),
				})}
			>
				<CardHeader
					title={
						<div className="flex flex-1 gap-2 items-center">
							<Typography fontWeight={700} fontSize={14}>
								{sender}
							</Typography>
							<span className="relative flex h-2 w-2">
								<span
									className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
									style={{
										backgroundColor: getStatusColor(
											message.user?.status || "available",
										),
									}}
								/>
								<span
									className="relative inline-flex rounded-full h-2 w-2"
									style={{
										backgroundColor: getStatusColor(
											message.user?.status || "available",
										),
									}}
								/>
							</span>
							{message?.mentioned_team_name && (
								<Chip label={message.mentioned_team_name} size="small" />
							)}
						</div>
					}
					action={
						<MessageAction
							flag={message._flag}
							created_at={message.created_at}
							unread_thread_messages={message.unread_thread_messages}
						/>
					}
					sx={{ p: 0, justifyContent: "center", alignItems: "flex-start" }}
				/>
				<CardContent>
					{!message.is_deleted ? (
						<Suspense>
							{message.preview ? (
								<PublishBox
									{...(message.preview as any)}
									className="!w-full my-2"
								/>
							) : null}
							{message.parent && onClickReply ? (
								<ReplyBox
									chatModule={chatModule}
									message={message.parent as unknown as MessageDiscussion}
									className="!w-full cursor-pointer"
									onClick={onClickReply}
								/>
							) : null}
							{message.document_count && message.documents?.length ? (
								<MediaBox message={message} />
							) : null}
						</Suspense>
					) : null}
					{message.hemi_responses?.[0]?.status === "pending" ? (
						<div className="flex justify-normal items-center space-x-2 py-4">
							<div className="w-3 h-3 bg-blue-700 rounded-full animate-bounce" />
							<div className="w-3 h-3 bg-blue-700 rounded-full animate-bounce animation-delay-200" />
							<div className="w-3 h-3 bg-blue-700 rounded-full animate-bounce animation-delay-400" />
						</div>
					) : null}
					{text ? (
						<Suspense>
							<MarkdownMessage
								text={text}
								members={memberMentions}
								keyword={searchQ}
								isStrike={message.strike}
								isDeleted={message.is_deleted}
								isMe={message._isMe}
							/>
						</Suspense>
					) : null}
				</CardContent>
				{messageQuery.isLoading ? (
					<CardActions className="!justify-end !items-end">
						<Skeleton className="w-full h-6" />
					</CardActions>
				) : null}
				{messages?.length && !messageQuery.isLoading ? (
					<CardActions className="!justify-end !items-center">
						<div className="flex-none">
							<AvatarGroup
								spacing="medium"
								total={messages.length}
								classes={{
									avatar: "!w-4 !h-4 !text-xs !text-dark !bg-zinc-200",
								}}
							>
								{messages
									.filter((_, ridx) => ridx <= 4)
									.map((r) => (
										<Avatar
											key={r.id}
											alt={r.user?.fullname}
											src={r.user?.avatar ?? undefined}
										>
											{String(r.user?.fullname)[0].toUpperCase()}
										</Avatar>
									))}
							</AvatarGroup>
						</div>
						{messages.length ? (
							<Typography fontWeight={500} fontSize={12}>
								{pluralize({
									count: messages.length,
									singularText: "Reply",
									pluralText: "Replies",
								})}
							</Typography>
						) : null}
						{messages?.at(-1) ? (
							<>
								<Typography fontWeight={400} fontSize={12}>
									{dayjs
										// @ts-expect-error
										.unix(messages?.at(-1)?.updated_at)
										.format("DD MMM YYYY, HH:mm A")}
								</Typography>
								{FlagIcons.read}
							</>
						) : null}
					</CardActions>
				) : null}
			</Card>
		</div>
	);
};

export default memo(MessageItem, (prevProps, nextProps) => {
	return isEqual(prevProps.message, nextProps.message);
});
